import React, { useCallback, Fragment, useMemo } from 'react'
import { BatchSalePrices } from './batch-price-edit'
import { useDynamicList } from 'ahooks'
import { ColumnType } from 'antd/lib/table'
import { Column, Row } from '@/components/layout/layout'
import { WithInputLimit, INPUT_NUMBER_LIMIT_REGEXP, INPUT_PRICE_LIMIT_REGEXP, get, isExist } from '@/utils'
import useBemClass from '@/utils/hooks/useBEMClass'
import { Table } from 'antd'
import './batch-price.scss'
import { BaseDialog } from '@/components/dialog/base-dialog'
import { layerManage } from '@/utils/layer'
import Button from '@/components/button/button'
import { useEnter } from '@/utils/hooks/useEnter'

interface LadderPricePopoverProps {
    salePrice: BatchSalePrices[]
    onConfirm: (price: BatchSalePrices[]) => void
}

const LadderPricePopover: React.FC<LadderPricePopoverProps> = props => {
    const cls = useBemClass('batch-price')

    const { list: price, replace, remove, push } = useDynamicList<BatchSalePrices>(props.salePrice)

    const getUnitError = useCallback(
        (val: number, index: number) => {
            if (val <= +get(price, `[${index - 1}].new_threshold`, 0) && index) {
                return '购买量需大于上一级'
            }

            return ''
        },
        [price],
    )

    const getPriceError = useCallback(
        (val: number, index: number) => {
            if (index && val >= +get(price, `[${index - 1}].new_sale_price`, 0)) {
                return '单价需小于上一级'
            }

            if (!val && index) {
                return '单价需大于0'
            }

            return ''
        },
        [price],
    )

    const enterConfirm = useEnter(() => {
        layerManage.pop()
        props.onConfirm(price)
    })

    const isDisabled = useMemo(() => {
        return isExist(
            price
                .map((p, i) => {
                    if (getPriceError(+p.new_sale_price, i) || getUnitError(+p.new_threshold, i)) {
                        return 1
                    }

                    return 0
                })
                .filter(Boolean),
        )
    }, [getPriceError, getUnitError, price])

    const columns: ColumnType<BatchSalePrices>[] = [
        {
            title: '购买量',
            align: 'center',
            width: 166,
            render: (val, record, index) => {
                return (
                    <Fragment>
                        {index === 0 ? (
                            <span>{'≥'}0</span>
                        ) : (
                            <Column data-padding-top-8>
                                <Row data-height-100p data-justify-content-center data-align-items-center>
                                    <span>{'≥'}</span>
                                    <div className={cls('ladder-ipts')}>
                                        {WithInputLimit(
                                            <input
                                                value={record.new_threshold}
                                                {...enterConfirm}
                                                onChange={e => {
                                                    replace(index, {
                                                        ...price[index],
                                                        new_threshold: e.target.value,
                                                    })
                                                }}
                                                placeholder="请输入"
                                                className={cls('ladder-ipt')}
                                                type="text"
                                            />,
                                            INPUT_NUMBER_LIMIT_REGEXP,
                                        )}
                                        <span>{record.sale_unit.name}</span>
                                    </div>
                                </Row>
                                <span data-padding-top-8 className={cls('error')}>
                                    {getUnitError(+record.new_threshold, index)}
                                </span>
                            </Column>
                        )}
                    </Fragment>
                )
            },
        },
        {
            title: '单价',
            width: 142,
            render: (text, record, index) => {
                return (
                    <Fragment>
                        {
                            <Column data-padding-top-8>
                                <Row data-height-100p data-justify-content-center data-align-items-center>
                                    <div className={cls('ladder-ipts')}>
                                        {WithInputLimit(
                                            <input
                                                {...enterConfirm}
                                                value={record.new_sale_price}
                                                onChange={e => {
                                                    replace(index, {
                                                        ...price[index],
                                                        new_sale_price: e.target.value,
                                                    })
                                                }}
                                                placeholder="请输入"
                                                className={cls('ladder-ipt')}
                                                type="text"
                                            />,
                                            INPUT_PRICE_LIMIT_REGEXP,
                                        )}
                                        <span>元/{record.sale_unit.name}</span>
                                    </div>
                                </Row>
                                <span data-padding-top-8 className={cls('error')}>
                                    {getPriceError(+record.new_sale_price, index)}
                                </span>
                            </Column>
                        }
                    </Fragment>
                )
            },
        },
        {
            title: '',
            width: 70,
            render: (text, record, index) => {
                return index === 0 ? null : (
                    <Row
                        data-justify-content-center
                        onClick={() => {
                            remove(index)
                        }}
                    >
                        <i style={{ color: '#f00' }} className="iconfont icon6yichu-20"></i>
                    </Row>
                )
            },
        },
    ]

    return (
        <BaseDialog
            styles={{
                minWidth: '420px',
            }}
            isShow
            title="改售价"
            onClose={() => layerManage.pop()}
        >
            <Column>
                <div className="table-container">
                    <Table
                        bordered
                        pagination={false}
                        rowKey={record => record.id}
                        columns={columns}
                        dataSource={price}
                        footer={
                            price.length < 3
                                ? () => (
                                      <Row
                                          data-align-items-center
                                          data-justify-content-center
                                          data-expaned
                                          onClick={() => {
                                              push({
                                                  id: 0,
                                                  sale_price: '',
                                                  threshold: '',
                                                  new_threshold: '',
                                                  new_sale_price: '',
                                                  sale_unit: price[0].sale_unit,
                                              })
                                          }}
                                      >
                                          <span className="text-btn-blue">+新增阶梯价</span>
                                      </Row>
                                  )
                                : void 0
                        }
                    ></Table>
                </div>
                <Row
                    data-width-100p
                    data-justify-content-flex-end
                    data-align-items-center
                    data-padding-right-15
                    data-padding-bottom-15
                >
                    <Button
                        disabled={isDisabled}
                        types="default"
                        onClick={() => {
                            props.onConfirm(price)
                            layerManage.pop()
                        }}
                    >
                        确定
                    </Button>
                </Row>
            </Column>
        </BaseDialog>
    )
}

export default LadderPricePopover
